import React, { useState } from "react";
import { KeyRound, AlertCircle } from "lucide-react";
import { toast } from "sonner";
import { changePassword } from "@/lib/api";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";

// "Changer mon mot de passe" — available to every signed-in member.
export const ChangePasswordButton = () => {
  const [open, setOpen] = useState(false);
  const [current, setCurrent] = useState("");
  const [next, setNext] = useState("");
  const [confirm, setConfirm] = useState("");
  const [error, setError] = useState("");
  const [pending, setPending] = useState(false);

  const reset = () => {
    setCurrent("");
    setNext("");
    setConfirm("");
    setError("");
  };

  const submit = async (e) => {
    e.preventDefault();
    if (pending) return;
    if (next.length < 12) {
      setError("Le nouveau mot de passe doit contenir au moins 12 caractères.");
      return;
    }
    if (next !== confirm) {
      setError("Les deux mots de passe ne correspondent pas.");
      return;
    }
    setError("");
    setPending(true);
    try {
      await changePassword({ current_password: current, new_password: next });
      toast.success("Mot de passe mis à jour");
      setOpen(false);
      reset();
    } catch (err) {
      const detail = err.response?.data?.detail;
      setError(typeof detail === "string" ? detail : "Impossible de changer le mot de passe.");
    } finally {
      setPending(false);
    }
  };

  return (
    <>
      <button
        type="button"
        data-testid="change-password-button"
        onClick={() => setOpen(true)}
        className="inline-flex items-center gap-2 rounded-lg border border-slate-200 bg-white text-slate-700 text-sm font-medium px-4 py-2 hover:border-emerald-300 hover:text-emerald-700 transition-colors shrink-0"
      >
        <KeyRound className="h-4 w-4" /> Changer mon mot de passe
      </button>

      <Dialog open={open} onOpenChange={(o) => { setOpen(o); if (!o) reset(); }}>
        <DialogContent className="sm:max-w-[400px]">
          <DialogHeader>
            <DialogTitle className="font-heading">Changer mon mot de passe</DialogTitle>
            <DialogDescription className="sr-only">Changer votre mot de passe de connexion</DialogDescription>
          </DialogHeader>
          <form onSubmit={submit} className="space-y-3">
            {error && (
              <div data-testid="change-password-dialog-error" role="alert" className="flex items-start gap-2 rounded-lg border border-red-200 bg-red-50 px-3 py-2.5 text-sm text-red-700">
                <AlertCircle className="h-4 w-4 mt-0.5 shrink-0" />
                <span>{error}</span>
              </div>
            )}
            <div>
              <Label htmlFor="cpd-current" className="text-sm">Mot de passe actuel</Label>
              <Input id="cpd-current" data-testid="change-current-input" type="password" autoComplete="current-password" required value={current} onChange={(e) => setCurrent(e.target.value)} className="mt-1 h-11 text-base" />
            </div>
            <div>
              <Label htmlFor="cpd-new" className="text-sm">Nouveau mot de passe</Label>
              <Input id="cpd-new" data-testid="change-new-input" type="password" autoComplete="new-password" required value={next} onChange={(e) => setNext(e.target.value)} className="mt-1 h-11 text-base" placeholder="12 caractères minimum" />
            </div>
            <div>
              <Label htmlFor="cpd-confirm" className="text-sm">Confirmer le nouveau mot de passe</Label>
              <Input id="cpd-confirm" data-testid="change-confirm-input" type="password" autoComplete="new-password" required value={confirm} onChange={(e) => setConfirm(e.target.value)} className="mt-1 h-11 text-base" />
            </div>
            <DialogFooter>
              <button
                type="submit"
                data-testid="change-password-dialog-submit"
                disabled={pending || !current || !next || !confirm}
                className="w-full sm:w-auto inline-flex items-center justify-center gap-2 rounded-lg bg-emerald-600 text-white text-sm font-semibold px-5 py-2.5 hover:bg-emerald-700 disabled:opacity-50"
              >
                {pending ? "Enregistrement…" : "Enregistrer"}
              </button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </>
  );
};
